import type { ProductCardProps } from '@packages/modules/src/ProductCard/types';
import type { EfficiencyFlagProps } from '@packages/shared/src/components/EfficiencyFlag';
import type { ActualSearchProductCardProps } from '../components/SearchProductCard/SearchProductCard';
import type { Flags, Product } from '../api/rest/types';

/**
 * Mapping energy efficency class to label color
 */
interface LabelColorsMap {
  [key: string]: string;
}

const labelColorsMap: LabelColorsMap = {
  a: 'darkgreen',
  b: 'mediumgreen',
  c: 'lightgreen',
  d: 'yellow',
  e: 'lightorange',
  f: 'darkorange',
  g: 'red',
};

type FilteredFlags = Omit<Flags, 'layout'> & {
  layout: Exclude<Flags['layout'], 'custom_large'>;
};

const mapOldFlagsToNewFlags = (flags?: Flags[]): ProductCardProps['flags'] =>
  flags
    ?.filter(
      // filter out custom flags which are added in NJS-541
      (flag): flag is FilteredFlags => flag.layout !== 'custom_large',
    )
    ?.map((flag) => ({
      text: flag.text || '',
      // data model changed, so we need to map the old layout to the new type
      src: flag.src || '',
      type: flag.layout === 'sustainable' ? 'sustainability' : flag.layout,
    }));

const mapAdditionalFlags = (product: Product): ProductCardProps['additionalFlags'] => {
  // energyEfficiencyClass becomes additionalFlags
  const efficencyClassName = product.energyEfficiencyClass?.name;
  const energyEfficiencyClass = efficencyClassName
    ? [
        {
          type: (product.energyEfficiencyClass?.hasOldLabel
            ? 'energyEfficiencyOld'
            : 'energyEfficiency') as EfficiencyFlagProps['type'],
          text: efficencyClassName,
          overlay: {
            title: efficencyClassName,
            imgUrl: product.energyEfficiencyClass?.image || '',
          },
          labelColor:
            product.energyEfficiencyClass?.color ??
            (labelColorsMap[efficencyClassName.toLowerCase()] as EfficiencyFlagProps['labelColor']),
        },
      ]
    : undefined;

  // componentEEKs become additionalFlags as well
  const componentEEKs = product.componentEEKs?.map((component) => ({
    type: (component.componentName ||
      (component.hasOldLabel
        ? 'energyEfficiencyOld'
        : 'energyEfficiency')) as EfficiencyFlagProps['type'],
    text: component.class,
    overlay: {
      // if component name is set, use it as title, otherwise use class
      title: component.component || component.class,
      imgUrl: component.image || '',
    },
    labelColor:
      component.color ??
      (labelColorsMap[component.class?.toLowerCase()] as EfficiencyFlagProps['labelColor']),
  }));

  return componentEEKs || energyEfficiencyClass; // if componentEEKs is set, energyEfficiencyClass are ignored
};

/**
 * Helper function to transform legacy search api data for new components props
 * @param products array of products
 * @param requestCacheKey cache key for request
 * @returns array of products with new structure
 */
export const transformProductsData = (
  products: Product[],
  requestCacheKey: string,
  categoryDataForTracking: ActualSearchProductCardProps['categoryDataForTracking'],
): ActualSearchProductCardProps[] =>
  products.map((product): ActualSearchProductCardProps => {
    const flags = mapOldFlagsToNewFlags(product.flags);

    return {
      product: {
        variations: product.variations,
        masterSku: product.masterSku,
        highlight: product.isHighlight,
        sku: product.masterSku,
        brand: product.brand.name,
        title: product.nameNoBrand,
        flags,
        additionalFlags: mapAdditionalFlags(product),
        additionalInfo: product.sellingPoints,
        datasheet: product.productDataSheet,
        productPosition: product.productPosition,
        mkz: product.mkz,
        avgRating: product.rating?.value,
      },
      requestCacheKey,
      categoryDataForTracking,
    };
  });
